import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Phone, Calendar, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [sidebarSubmitted, setSidebarSubmitted] = useState(false);
  const faqs = [{
    question: 'Do you charge a call-out fee?',
    answer: "We provide upfront pricing before any work begins. Contact us for a quote and we'll give you a clear, honest price with no hidden extras."
  }, {
    question: 'Are you available for after-hours emergencies?',
    answer: 'Yes. For genuine electrical emergencies including power outages, sparking outlets, and unsafe switchboards, call Brodie directly on 0418 381 988.'
  }, {
    question: 'How quickly can you respond?',
    answer: 'Most standard jobs are quoted within 24 hours and completed same-day or within 48 hours. Emergencies are prioritised.'
  }, {
    question: 'Do you offer a warranty on your work?',
    answer: "Yes. All work is backed by the Completely Electrical Guarantee. If you're not satisfied, we'll make it right."
  }, {
    question: 'What areas of Perth do you service?',
    answer: "We're based in Huntingdale and service Perth's South Of River (SOR) region. Priority suburbs: Gosnells, Thornlie, Maddington, Southern River, Harrisdale, Leeming, Cockburn. We also regularly service Mandurah and Golden Bay."
  }, {
    question: 'Can you upgrade the lighting in my newly built home?',
    answer: 'Absolutely, this is one of our specialties. We install premium pendants, LED strip lighting, downlights and feature lighting to transform builder-standard new homes.'
  }, {
    question: 'Do you install split system and evaporative air conditioning?',
    answer: "Yes. We supply, install and service both split system and evaporative air conditioners for residential and commercial properties across Perth's south."
  }, {
    question: 'Do you fix and install hot water systems?',
    answer: 'Yes. We install, repair and replace electric hot water systems. Hot water emergencies are prioritised for same-day attendance where possible.'
  }, {
    question: 'Can you issue a Safety Certificate or do a Compliance Check?',
    answer: 'Yes. We carry out electrical compliance checks and issue safety certificates for residential and commercial properties, useful before selling, renting out, or after renovations.'
  }, {
    question: 'Do you install and test smoke alarms? What about WA legal requirements?',
    answer: 'Yes. We install, inspect and test smoke alarms in line with WA Building Regulations. Landlords and sellers have specific smoke alarm obligations, we can talk you through them.'
  }, {
    question: 'Do you do commercial and industrial electrical work?',
    answer: 'Yes. We work across domestic, commercial and industrial sectors, including switchboard upgrades, lighting and power, fault finding, and compliance for builders, mechanics, sheds and commercial premises.'
  }, {
    question: "What's your ABN and license status?",
    answer: 'ABN 40 434 878 561. We are a fully licensed WA electrical contractor (EC number available on request). All work meets EnergySafety WA and Australian Standards.'
  }];
  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section className="py-20 lg:py-28 bg-white" id="faq" data-id="element-15">
      <div className="max-w-7xl mx-auto px-4" data-id="element-16">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-17">
          <span className="text-accent font-bold tracking-wider uppercase text-sm mb-3 block" data-id="element-18">
            Common Questions
          </span>
          <h2 className="text-4xl md:text-5xl font-heading font-extrabold text-navy mb-6 tracking-tighter" data-id="element-19">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-slate leading-relaxed" data-id="element-20">
            Clear, honest answers to help you make a confident decision.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start" data-id="element-21">
          <div className="lg:col-span-8 space-y-3" data-id="element-22">
            {faqs.map((faq, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3,
            delay: index * 0.04
          }} className={`bg-white border rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 ${openIndex === index ? 'border-accent ring-1 ring-accent/20' : 'border-gray-200'}`} data-id="element-23">
                <button className="w-full px-7 py-5 text-left flex justify-between items-center focus:outline-none" onClick={() => toggleFAQ(index)} data-id="element-24">
                  <span className="font-heading font-extrabold text-navy text-base pr-8" data-id="element-25">
                    {faq.question}
                  </span>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${openIndex === index ? 'bg-accent/10' : 'bg-gray-50'}`} data-id="element-26">
                    {openIndex === index ? <ChevronUp className="w-5 h-5 text-accent" data-id="element-27" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-28" />}
                  </div>
                </button>

                <AnimatePresence data-id="element-29">
                  {openIndex === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} className="overflow-hidden" data-id="element-30">
                      <div className="px-7 pb-6 text-slate text-base leading-relaxed border-t border-gray-50 pt-4" data-id="element-31">
                        {faq.answer}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>)}
          </div>

          {/* Sticky CTA Sidebar */}
          <div className="lg:col-span-4 lg:sticky lg:top-32" data-id="element-32">
            <div className="bg-navy rounded-2xl p-7 shadow-2xl border border-white/10 relative overflow-hidden" data-id="element-33">
              <div className="absolute top-0 right-0 w-32 h-32 bg-accent/20 blur-2xl rounded-full" data-id="element-34"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 blur-2xl rounded-full" data-id="element-35"></div>

              <div className="relative z-10" data-id="element-36">
                <AnimatePresence mode="wait" data-id="element-37">
                  {!sidebarSubmitted ? <motion.div key="sidebar-form" initial={{
                  opacity: 1
                }} exit={{
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.3
                }} data-id="element-38">
                      <h3 className="text-2xl font-heading font-extrabold text-white mb-3" data-id="element-39">
                        Still Have Questions?
                      </h3>
                      <p className="text-gray-200 mb-6 text-base" data-id="element-40">
                        Our team is ready to help, no obligation, no pressure.
                      </p>

                      <a href="tel:0418381988" className="w-full bg-accent hover:bg-accent-600 text-white px-6 py-4 rounded-xl font-extrabold text-lg flex items-center justify-center gap-3 transition-all shadow-lg hover:-translate-y-1 mb-4" data-id="element-41">
                        <Phone className="w-5 h-5" data-id="element-42" />
                        Call 0418 381 988
                      </a>

                      <div className="flex items-center gap-4 my-5" data-id="element-43">
                        <div className="h-px bg-white/20 flex-1" data-id="element-44"></div>
                        <span className="text-gray-300 font-bold text-sm uppercase" data-id="element-45">
                          OR
                        </span>
                        <div className="h-px bg-white/20 flex-1" data-id="element-46"></div>
                      </div>

                      <form className="space-y-3" onSubmit={e => {
                    e.preventDefault();
                    setSidebarSubmitted(true);
                  }} data-id="element-47">
                        <input type="text" placeholder="Your Name" className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all font-medium text-sm" data-id="element-48" />
                        <input type="tel" placeholder="Phone Number" className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all font-medium text-sm" data-id="element-49" />
                        <button type="submit" className="w-full bg-white text-navy hover:bg-gray-50 font-extrabold py-3.5 rounded-xl transition-all text-base flex items-center justify-center gap-2" data-id="element-50">
                          <Calendar className="w-5 h-5" data-id="element-51" />
                          Request Callback
                        </button>
                      </form>
                    </motion.div> : <motion.div key="sidebar-confirmation" initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.4
                }} className="text-center py-4" data-id="element-52">
                      <div className="w-14 h-14 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-53">
                        <CheckCircle className="w-7 h-7 text-emerald-400" data-id="element-54" />
                      </div>
                      <h3 className="text-xl font-heading font-extrabold text-white mb-2" data-id="element-55">
                        Request Received!
                      </h3>
                      <p className="text-gray-300 text-sm mb-4 leading-relaxed" data-id="element-56">
                        Brodie will text you within{' '}
                        <strong className="text-white" data-id="element-57">15 minutes</strong> to
                        help with your question.
                      </p>
                      <a href="tel:0418381988" className="inline-flex items-center gap-2 text-accent font-bold text-sm hover:text-accent-300 transition-colors" data-id="element-58">
                        <Phone className="w-4 h-4" data-id="element-59" />
                        Need us sooner? Call 0418 381 988
                      </a>
                      <button onClick={() => setSidebarSubmitted(false)} className="block mx-auto mt-3 text-xs text-gray-400 hover:text-white transition-colors" data-id="element-60">
                        Submit another request
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}